import React, { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react'
import { useAuth0 } from '@auth0/auth0-react'

import ScrubstrApiQueryService from '../services/ScrubstrApiQueryService/ScrubstrApiQueryService'
import { AuthStateContext } from './AuthStateContext'

export type AdminSession = {
  isAdmin: boolean
  permissions: string[]
  user: {
    id: string
    email: string
    name: string
    picture?: string
  }
}

type AdminAccessContextType = {
  session: AdminSession | null
  isLoading: boolean
  isAdmin: boolean
  refreshSession: () => Promise<void>
}

const AdminAccessContext = createContext<AdminAccessContextType>({
  session: null,
  isLoading: true,
  isAdmin: false,
  refreshSession: async () => undefined,
})

export const useAdminAccess = (): AdminAccessContextType => useContext(AdminAccessContext)

type AdminAccessProviderProps = { children: React.ReactNode }

export const AdminAccessProvider = ({ children }: AdminAccessProviderProps): JSX.Element => {
  const { isAuthenticated, isLoading: isAuth0Loading } = useAuth0()
  const [session, setSession] = useState<AdminSession | null>(null)
  const [isLoading, setIsLoading] = useState(true)

  const refreshRequestIdRef = useRef(0)
  const inFlightRef = useRef<Promise<void> | null>(null)

  const refreshSession = useCallback(async () => {
    if (!isAuthenticated) {
      setSession(null)
      setIsLoading(false)
      return
    }

    if (!AuthStateContext.getToken()) {
      setIsLoading(false)
      return
    }

    if (inFlightRef.current) {
      return inFlightRef.current
    }

    const run = async () => {
      const requestId = ++refreshRequestIdRef.current
      setIsLoading(true)

      try {
        const api = new ScrubstrApiQueryService()
        const response = await api.post<AdminSession>({ endpoint: 'user/admin-session' })

        if (requestId === refreshRequestIdRef.current) {
          setSession(response)
        }
      } catch (error) {
        console.error('Failed to establish admin session', error)

        if (requestId === refreshRequestIdRef.current) {
          setSession((prev) => {
            if (prev?.isAdmin) {
              return prev
            }

            return {
              isAdmin: false,
              permissions: [],
              user: { id: '', email: '', name: '' },
            }
          })
        }
      } finally {
        if (requestId === refreshRequestIdRef.current) {
          setIsLoading(false)
        }
      }
    }

    inFlightRef.current = run().finally(() => {
      inFlightRef.current = null
    })

    return inFlightRef.current
  }, [isAuthenticated])

  useEffect(() => {
    if (isAuth0Loading) {
      return
    }

    refreshSession()
  }, [isAuth0Loading, isAuthenticated, refreshSession])

  return (
    <AdminAccessContext.Provider
      value={{
        session,
        isLoading: isAuth0Loading || isLoading,
        isAdmin: session?.isAdmin ?? false,
        refreshSession,
      }}
    >
      {children}
    </AdminAccessContext.Provider>
  )
}
